'use client';
import {
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
} from 'react';

// Components
import { AtNavLink } from '@/components/atoms/at-nav-link/at-nav-link';
import { AtClickable } from '@/components/atoms/at-clickable/at-clickable';
import { AtIcon } from '@/components/atoms/at-icon/at-icon';

// Types
import {
  EClickableSize,
  EClickableTarget,
  EClickableVariants,
} from '@/components/atoms/at-clickable/at-clickable.types';
import { EIcons } from '@/components/atoms/at-icon/icons';
import { ENavLinkVariants } from '@/components/atoms/at-nav-link/at-nav-link.types';
import mlModal from './ml-modal.variants';
import {
  EModalVariants,
  IMlModalProps,
  IModalRefProps,
} from './ml-modal.types';

// Hooks + Utils
import { useFocusTrap } from '@mantine/hooks';
import useBreakpoint from '@/hooks/useBreakpoint';
import classNames from 'classnames';
import useBodyLock from './hooks/useBodyLock';

/**
 * MlModal is a flexible, variant-driven modal component that renders a <dialog> element
 * with scroll-lock, focus-trap, and variant-based layout behavior.
 *
 * Should be used in conjunction with `MlModalWrapper`, which manages its lifecycle
 * and handles open/close behavior via a forwarded ref.
 *
 */
export const MlModal = forwardRef(
  (
    {
      children,
      onOpen,
      onClose,
      className,
      variant = EModalVariants.DEFAULT,
    }: IMlModalProps,
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const closeRef = useRef<HTMLButtonElement>(null);
    const dialogRef = useRef<HTMLDialogElement>(null);

    const openModal = () => {
      dialogRef?.current?.showModal();
      onOpen?.();
      setIsOpen(true);
    };

    useBodyLock(isOpen, setIsOpen);

    const closeModal = () => {
      onClose?.();
      dialogRef?.current?.close();
      setIsOpen(false);
    };

    // Exposes these fns + element externally to make sure we can contain state to the component, but be able to control it using ref in parent
    useImperativeHandle(
      ref,
      (): IModalRefProps => ({
        handleOpen: openModal,
        handleClose: closeModal,
        dialogRef: dialogRef?.current,
        isOpen: isOpen,
      }),
    );

    useEffect(() => {
      if (isOpen && closeRef.current) {
        closeRef.current.focus();
      }
    }, [isOpen]);

    const { dialog, childrenContainer } = mlModal({ variant });

    const bp = useBreakpoint();

    const isMobile = bp === 'xs' || bp === 'sm';

    const focusTrapRef = useFocusTrap(isOpen);
    return (
      <dialog
        ref={dialogRef}
        className={classNames(dialog(), className)}
        autoFocus={true}
      >
        <div ref={focusTrapRef}>
          <header className="flex flex-row items-center justify-end">
            {variant === EModalVariants.SLIDESHOW && !isMobile ? (
              <AtClickable
                variant={EClickableVariants.PRIMARY}
                outline
                onClick={closeModal}
                interactLocation="close modal"
                shouldSendGTMEvent={false}
                label="Close Slideshow"
              />
            ) : (
              <AtNavLink
                size={EClickableSize.SM}
                target={EClickableTarget.SELF}
                variant={ENavLinkVariants.CTA}
                aria-label={isOpen ? 'Close Dialog' : 'Open Dialog'}
                className="min-w-auto z-20 !m-0 box-border w-auto bg-transparent !p-0 text-left text-3xl lg:ml-0"
                onClick={closeModal}
                ref={closeRef}
                shouldSendGTMEvent={false}
              >
                <span className="text-32 lg:text-58">
                  <AtIcon
                    icon={EIcons.NAV_CLOSE}
                    className="rounded-full bg-skin-ui-fill-primary"
                  />
                </span>
              </AtNavLink>
            )}
          </header>
          <div className={classNames('mt-6 lg:mt-6.5', childrenContainer())}>
            {children}
          </div>
        </div>
      </dialog>
    );
  },
);

MlModal.displayName = 'MlModal';
